import React, { useState, useEffect } from 'react'
import { SwapOutlined } from '@ant-design/icons';
import Select from 'react-select';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Checkbox, Space, Tabs, Tag, Modal, Input } from 'antd';
import { toast } from 'react-toastify';
import config from '../../../config';
import MouvementEchange from '../../mouvement/MouvementEchange';
import {
  NdoePage, NdoePageHeader, NdoeStats, NdoeStatCard,
  NdoeResponsiveTable, NdoeDataCard, NdoeActionButton, NdoeImage,
} from '../../../components/ndoe';
import useMontantsEchange from '../../../hooks/useMontantsEchange';

const EchangeForm = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [data, setData] = useState({})
  const [commande, setCommande] = useState([]);
  const [getCommande, setGetCommande] = useState([]);
  const [value, setValue] = useState(null);
  const [selected, setSelected] = useState([]);
  // « Traiter » une demande livreur : { id_commande, id_varianteProduit } à pré-cocher
  // une fois les articles de CETTE commande chargés (évite la mauvaise paire à la main).
  // La commande est portée avec la variante → garde anti-course fiable.
  const [cible, setCible] = useState(null);
  const [demandes, setDemandes] = useState([]);   // demandes d'échange signalées par les livreurs (Cas A)
  const [activeTab, setActiveTab] = useState('0');
  // Annulation admin : cible + motif saisis dans une modale (remplace window.prompt).
  const [annulTarget, setAnnulTarget] = useState(null);
  const [motif, setMotif] = useState('');
  const [annulLoading, setAnnulLoading] = useState(false);
  const navigate = useNavigate();
  // `isAdmin` garde l'ANNULATION d'une demande (geste tracé, réservé côté
  // serveur) — à ne pas confondre avec le droit de LIRE les montants, qui a sa
  // propre règle depuis le 30/08/2026.
  const isAdmin = useSelector((s) => s.user?.currentUser?.role) === 'admin';
  const peutVoirMontants = useMontantsEchange();

  // Annulation admin d'une demande signalée par erreur (motif obligatoire, tracé serveur).
  const openAnnulModal = (d) => { setAnnulTarget(d); setMotif(''); };

  const confirmAnnuler = async () => {
    if (!motif || !motif.trim()) { toast.error('Le motif est obligatoire.'); return; }
    const d = annulTarget;
    if (!d) return;
    try {
      setAnnulLoading(true);
      await axios.put(`${DOMAIN}/api/livraison/echanges-demandes/${d.id_detail_livraison}/annuler`, { motif });
      toast.success('Demande annulée.');
      setDemandes((prev) => prev.filter((x) => x.id_detail_livraison !== d.id_detail_livraison));
      setAnnulTarget(null);
      setMotif('');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Annulation impossible.');
    } finally {
      setAnnulLoading(false);
    }
  };

  const handleInputChange = async (e) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;

    let updatedValue = fieldValue;

    setData((prev) => ({ ...prev, [fieldName]: updatedValue }));
  };

  // Sélection UNIQUE : cocher une ligne REMPLACE la précédente. L'URL /echange/:id
  // ne traite qu'un détail à la fois ; plusieurs cases cochées donnaient l'illusion
  // d'un multi-échange alors que seule la 1re partait (P0). Décocher vide.
  const handleSelectionChange = (event, id_vente, id_commande, id_detail_commande, prix_unitaire, id_taille, id_client, id_varianteProduit) => {
    if (event.target.checked) {
      setSelected([{ id_vente, id_commande, id_detail_commande, prix_unitaire, id_taille, id_client, id_varianteProduit }]);
    } else {
      setSelected([]);
    }
  };

  // Une seule ligne peut être choisie → on ouvre l'échange sur elle.
  const handleChanger = () => {
    if (!selected.length) return;
    const choix = selected[0];
    // On transmet AUSSI la variante de la paire rendue : l'écran d'échange en a
    // besoin pour vérifier « même pointure/couleur » contre la VRAIE origine
    // (avant, il comparait le remplacement à lui-même — cf. EchangeCommande).
    const origine = choix.id_varianteProduit ? `&id_variante_origine=${choix.id_varianteProduit}` : '';
    navigate(`/echange/${value}?id_detail=${choix.id_detail_commande}${origine}`);
  };

  const columns = [
    {
      title: '',
      dataIndex: 'id_detail',
      key: 'selected',
      width :'60px',
      render: (text, record) => (
        <div>
          <Checkbox
            checked={selected.some((item) => item.id_detail_commande === record.id_detail_commande)}
            onChange={(event) =>
              handleSelectionChange(event,record.id_vente,record.id_commande, record.id_detail_commande, record.prix_unitaire,record.id_taille,record.id_client,record.id_varianteProduit)
            }
          />
        </div>
      ),
    },
    { title: '#', dataIndex: 'id', key: 'id', render: (text, record, index) => index + 1, width:"2%"},
      {
        title: 'image',
        dataIndex: 'img',
        key: 'image',
        width: '20px',
        render: (text, record) => (
          <div className="userList" onClick={()=>navigate(`/pageLivreurDetail/${record.id_varianteProduit}`)}>
            <NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />
          </div>
        )
    },
    {
      // Mandat §6 : référence + libellé sur la ligne à échanger.
      title: 'Article',
      dataIndex: 'nom_produit',
      key: 'article',
      render: (text, record) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {(record.code_article || record.code_produit) && (
            <span className="num" style={{ color: '#8a5a10', fontWeight: 700, fontSize: 12 }}>
              {record.code_article || record.code_produit}
            </span>
          )}
          <span>{text || '—'}</span>
        </div>
      ),
    },
    {
      title: 'Pointure',
      dataIndex: 'pointure',
      key: 'pointure',
      render: (text) => (
        <Space>
          <Tag color="blue">{text}</Tag>
        </Space>
      ),
    },
    {
      title: 'Prix',
      dataIndex: 'prix_unitaire',
      key: 'prix_unitaire',
      sorter: (a, b) => a.prix_unitaire - b.prix_unitaire,
      sortDirections: ['descend', 'ascend'],
      render: (text) => (
        <span>
        <Tag color={'blue'}>
          {parseFloat(text).toLocaleString('fr-FR', {
            style: 'currency',
            currency: 'USD',
          })}
        </Tag>
        </span>
      ),
    },
    {
      title: 'Couleur',
      dataIndex: 'description',
      key: 'description',
      render: (color) => {
        let tagColor;

        if (color === 'Rouge') {
          tagColor = '#FF0000';
        } else if (color === 'Noir') {
          tagColor = 'black';
        } else if (color === 'Noir brillant'){
          tagColor = '#000';
        } else if (color === 'Orange') {
          tagColor = 'orange';
        } else if (color === 'Bleu') {
          tagColor = 'skyblue';
        } else if (color === 'Bleu ciel'){
          tagColor = '#87CEEB';
        } else if (color === 'Chocolat') {
          tagColor = 'chocolate';
        } else if (color === 'Vert fluo') {
          tagColor = 'lime';
        } else if (color === 'Vert') {
          tagColor = ' #008000';
        }else if (color === 'Vert clair'){
          tagColor = '#90EE90';
        } else if (color === 'Rose fuschia') {
          tagColor = '#FF00FF';
        }else if (color === 'Rose'){
          tagColor = '#FFC0CB';
        } else if (color === 'Beige saumon') {
          tagColor = 'burlywood';
        } else if (color === 'Jaune') {
          tagColor = 'yellow';
        } else if (color === 'Gris') {
          tagColor = 'gray';
        } else if (color === 'Violet') {
          tagColor = 'purple';
        } else if (color === 'Mauve') {
          tagColor = '#D473D4';
        } else if (color === 'Argente'){
          tagColor = '#C0C0C0';
        } else if (color === 'Dorée'){
          tagColor = '#C0C0C0';
        }else if (color === 'Rouge Bordeau'){
          tagColor = '#6D071A';
        }else if (color === 'Beige'){
          tagColor = '#F5F5DC';
        }else if (color === 'Marron fonce'){
          tagColor = '#800000';
        }else if (color === 'Marron'){
          tagColor = '#A52A2A';
        }else if (color === 'Blanc'){
          tagColor = 'white';
        } else {
          // Couleur par défaut si aucune correspondance n'est trouvée
          tagColor = 'default';
        }

        return (
          <Tag color={tagColor}>{color}</Tag>
        );
      },
    },
    {
      title: 'Qté',
      dataIndex: 'quantite',
      key: 'quantite',
      render: (text) => (
        <Space>
          <Tag color="blue">{text}</Tag>
        </Space>
      ),
    }
  ].filter((col) => col.key !== 'prix_unitaire' || peutVoirMontants);

  // Carte mobile d'une ligne de commande échangeable (miroir des colonnes desktop).
  // Le prix suit la MÊME garde que la colonne desktop — c'est le point : les
  // deux rendus d'une même donnée ne doivent jamais diverger sur le droit.
  const renderGetCommandeCard = (record) => {
    const isSelected = selected.some((item) => item.id_detail_commande === record.id_detail_commande);
    const metrics = [
      { value: record.pointure ?? '—', label: 'pointure' },
      { value: record.description || '—', label: 'couleur' },
      { value: record.quantite ?? '—', label: 'qté' },
    ];
    if (peutVoirMontants) {
      metrics.push({
        value: parseFloat(record.prix_unitaire).toLocaleString('fr-FR', { style: 'currency', currency: 'USD' }),
        label: 'prix',
      });
    }
    return (
      <NdoeDataCard
        media={
          <div onClick={() => navigate(`/pageLivreurDetail/${record.id_varianteProduit}`)} style={{ cursor: 'pointer' }}>
            <NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />
          </div>
        }
        title={record.nom_produit || '—'}
        subtitle={record.code_article || record.code_produit || ''}
        metrics={metrics}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <Checkbox
            checked={isSelected}
            onChange={(event) =>
              handleSelectionChange(event, record.id_vente, record.id_commande, record.id_detail_commande, record.prix_unitaire, record.id_taille, record.id_client, record.id_varianteProduit)
            }
          />
          <span>Sélectionner pour l'échange</span>
        </label>
      </NdoeDataCard>
    );
  };

  // Actions d'une demande livreur (desktop + mobile) — garde admin sur « Annuler ».
  const demandeActions = (d) => [
    <NdoeActionButton
      key="t"
      size="sm"
      variant="ink"
      icon={<SwapOutlined />}
      onClick={() => { setCible({ id_commande: d.id_commande, id_varianteProduit: d.id_varianteProduit }); handleInputChange({ target: { name: 'id_commande', value: d.id_commande } }); setActiveTab('0'); }}
    >
      Traiter
    </NdoeActionButton>,
    isAdmin && (
      <NdoeActionButton key="a" size="sm" variant="danger" onClick={() => openAnnulModal(d)}>
        Annuler
      </NdoeActionButton>
    ),
  ].filter(Boolean);

  const demandeColumns = [
    { title: 'Cliente', dataIndex: 'nom_client', key: 'c' },
    { title: 'Article', key: 'a', render: (_, d) => `${d.code_article || d.code_produit ? `${d.code_article || d.code_produit} – ` : ''}${d.nom_produit || ''} · P.${d.taille ?? '?'}${d.couleur ? ' · ' + d.couleur : ''}` },
    { title: 'Qté', dataIndex: 'quantite', key: 'q', width: 55 },
    { title: 'Note du livreur', dataIndex: 'note_livreur', key: 'n' },
    { title: 'Livreur', dataIndex: 'livreur', key: 'l' },
    { title: '', key: 'act', render: (_, d) => <Space>{demandeActions(d)}</Space> },
  ];

  const renderDemandeCard = (d) => (
    <NdoeDataCard
      title={d.nom_client || 'Cliente'}
      subtitle={`${d.code_article || d.code_produit ? `${d.code_article || d.code_produit} – ` : ''}${d.nom_produit || ''} · P.${d.taille ?? '?'}${d.couleur ? ' · ' + d.couleur : ''}`}
      metrics={[
        { value: d.quantite ?? '—', label: 'qté' },
        { value: d.livreur || '—', label: 'livreur' },
      ]}
      actions={demandeActions(d)}
    >
      {d.note_livreur ? (
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ndoe-muted, #666)' }}>Note : {d.note_livreur}</p>
      ) : null}
    </NdoeDataCard>
  );

  useEffect(()=> {
    setValue(data?.id_commande);
    // Changement de commande → on ne garde JAMAIS la ligne cochée ni les articles
    // de la commande précédente (sinon « Changer » partait vers /echange/<nouvelle>
    // ?id_detail=<ancienne ligne> = MAUVAISE paire).
    setSelected([]);
    setGetCommande([]);
    // Si on s'éloigne de la commande ciblée par « Traiter » sans l'avoir traitée
    // (ex. chargement échoué → cible restée en suspens), on l'abandonne pour ne pas
    // pré-cocher par surprise une AUTRE commande. (Au clic « Traiter », data.id_commande
    // vient d'être fixé à la même valeur → la cible n'est pas abandonnée.)
    setCible((c) => (c && Number(c.id_commande) !== Number(data?.id_commande) ? null : c));
  }, [data?.id_commande])

  // Pré-sélection EXACTE après « Traiter » : dès que les articles échangeables de
  // la commande CIBLÉE sont chargés, on coche la ligne dont la variante = celle
  // demandée. La cible porte SON id_commande (pas l'état retardé `value`) → la garde
  // anti-course compare les articles chargés à la commande réellement ciblée.
  useEffect(() => {
    if (!cible || !getCommande.length) return;
    if (Number(getCommande[0]?.id_commande) !== Number(cible.id_commande)) return;
    const ligne = getCommande.find((r) => Number(r.id_varianteProduit) === Number(cible.id_varianteProduit));
    if (ligne) {
      setSelected([{
        id_vente: ligne.id_vente, id_commande: ligne.id_commande, id_detail_commande: ligne.id_detail_commande,
        prix_unitaire: ligne.prix_unitaire, id_taille: ligne.id_taille, id_client: ligne.id_client, id_varianteProduit: ligne.id_varianteProduit,
      }]);
    } else {
      toast.warning("La paire demandée n'apparaît pas dans les articles échangeables de cette commande — vérifie la sélection.");
    }
    setCible(null);
  }, [getCommande, cible])


  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/commande/commandeEchange`);
        setCommande(data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();

    const timeoutId = setTimeout(fetchData, 4000);

    return () => clearTimeout(timeoutId);
  }, [DOMAIN]);


  useEffect(() => {
    if (!value) return; // pas d'appel tant qu'aucune commande n'est choisie (évite .../echanges/undefined → 500)
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/produit/echanges/${value}`);
        setGetCommande(data);
      } catch (error) {
        setGetCommande([]);
        toast.error("Impossible de charger les articles de cette commande.");
      }
    };
    fetchData();
  }, [DOMAIN,value]);

  // Demandes d'échange signalées par les livreurs (Cas A — Phase 1), pour le staff.
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/livraison/echanges-demandes`);
        setDemandes(Array.isArray(data) ? data : []);
      } catch (e) { /* section non bloquante */ }
    };
    load();
  }, [DOMAIN]);

  const tabItems = [
    {
      key: '0',
      label: 'Echange',
      children: (
        <div className="ndoe-card">
          <div className="form-controle">
            <label htmlFor="id_commande">Sélectionnez une commande <span style={{ color: 'red' }}>*</span></label>
            <Select
              name="id_commande"
              options={commande?.map(item => ({ value: item.id_commande, label: item.nom + " Commande N° " + item.id_commande }))}
              onChange={selectedOption => handleInputChange({ target: { name: 'id_commande', value: selectedOption.value } })}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'demandes',
      label: `Demandes livreurs${demandes.length ? ` (${demandes.length})` : ''}`,
      children: (
        <NdoeResponsiveTable
          data={demandes}
          columns={demandeColumns}
          rowKey={(d) => d.id_detail_livraison}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 8 }}
          renderCard={renderDemandeCard}
          empty={{ icon: '📭', title: 'Aucune demande', text: "Aucune demande d'échange en attente." }}
        />
      ),
    },
    {
      key: '1',
      label: "Liste d'Echanges",
      children: <MouvementEchange />,
    },
  ];

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Logistique"
        title="Ajouter un échange"
        subtitle="Créer un nouvel échange"
      />

      <NdoeStats>
        <NdoeStatCard label="Demandes livreurs" value={demandes.length} />
        <NdoeStatCard label="Articles de la commande" value={getCommande.length} />
        <NdoeStatCard label="Sélectionnés" value={selected.length} />
      </NdoeStats>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      {getCommande.length !== 0 && (
        <>
          <NdoeResponsiveTable
            data={getCommande}
            columns={columns}
            rowKey={(r) => r.id_detail_commande}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 10 }}
            renderCard={renderGetCommandeCard}
            empty={{ icon: '📦', title: 'Aucun article', text: "Cette commande ne contient pas d'articles échangeables." }}
          />
          {selected.length > 0 && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <NdoeActionButton variant="primary" icon={<SwapOutlined />} onClick={handleChanger}>
                Changer cet article
              </NdoeActionButton>
            </div>
          )}
        </>
      )}

      {/* Annulation admin d'une demande signalée par erreur (motif obligatoire). */}
      <Modal
        title="Annuler la demande d'échange"
        open={!!annulTarget}
        onCancel={() => { setAnnulTarget(null); setMotif(''); }}
        onOk={confirmAnnuler}
        okText="Confirmer l'annulation"
        cancelText="Fermer"
        okButtonProps={{ danger: true, loading: annulLoading }}
        confirmLoading={annulLoading}
      >
        <p>Annuler la demande d'échange de {annulTarget?.nom_client || 'cette cliente'} ?</p>
        <label htmlFor="motif-annulation">Motif (obligatoire) :</label>
        <Input.TextArea
          id="motif-annulation"
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          rows={3}
          placeholder="Précisez le motif de l'annulation…"
          style={{ marginTop: 6 }}
        />
      </Modal>
    </NdoePage>
  )
}

export default EchangeForm
